/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { KTSVG } from '../../../../../_metronic/helpers';
import { Loading } from '../../Loadings/loading';
import { useToast } from '../../Toats';
import { useAuth } from '../../../../modules/auth';
import { generateHexSequence } from '../../../../utils/generateNumberHex';
import { getArchivesPlantOne, getArchivesService, postArchivePlantOne } from '../../../../services/archivesService';
import { ModalDeleteArquivos } from '../../modalPlant/components/ArchivesPlant/Components/ModalDeleteArquives';

type Props = {
  itemOrderOfService: any;
}

const iconMap: Record<string, string> = {
    jpg: 'bi-filetype-jpg',
    jpeg: 'bi-filetype-jpg',
    png: 'bi-filetype-png',
    pdf: 'bi-filetype-pdf',
    doc: 'bi-filetype-doc',
    docx: 'bi-filetype-doc',
    xls: 'bi-filetype-xls',
    xlsx: 'bi-filetype-xls',
    ppt: 'bi-filetype-ppt',
    pptx: 'bi-filetype-ppt',
    txt: 'bi-filetype-txt',
    zip: 'bi-file-earmark-zip',
};

const getIconClass = (description?: string) => {
    const ext = description ? (description.split('.').pop() || '').toLowerCase() : '';
    return iconMap[ext] || 'bi-file-earmark';
}

export function ArchivesOS({itemOrderOfService}: Props) {
    const idService = itemOrderOfService && itemOrderOfService.id ? itemOrderOfService.id : "";
    const [loadingPost, setLoadingPost] = useState<boolean>(false);
    const [isCardOpen, setIsCardOpen] = useState<boolean>(true);
    const [loadingOpenArchive, setLoadingOpenArchive] = useState<boolean>(false);
    const [idArchiveSelected, setIdArchiveSelected] = useState<string | null>(null);
    const [showModalDelete, setShowModalDelete] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { toggleToast } = useToast();
    const { currentUser } = useAuth();

    const { data: archives = [], isLoading: loading, refetch } = useQuery(
        ["getArchivesOS", idService],
        async () => {
            const archives = await getArchivesService(idService);
            return archives;
        },
        {
          enabled: !!idService,
          onError: (error) => {
            console.log(error);
          },
        }
    )

    const postArchive = async (file: File | undefined) => {
        if(!file) return;
        try {
            setLoadingPost(true)
            const formData = new FormData();
            formData.append("file", file);
            formData.append('key', `${generateHexSequence(10)}-${file.name}`);
            formData.append('description', file.name);
            formData.append('idService', idService);

            await postArchivePlantOne(formData)
            toggleToast("Sucesso ao anexar o arquivo. ", "success")
        } catch (error) {
            console.log(error)
            toggleToast("Erro ao anexar o arquivo. ", "error")
        } finally {
            setLoadingPost(false)
            if(fileInputRef.current) fileInputRef.current.value = "";
            refetch()
        }
    }

    const openArchive = async (idArchive: string) => {
        try {
            setIdArchiveSelected(idArchive)
            setLoadingOpenArchive(true)
            const myArchive = await getArchivesPlantOne(idArchive);
            if(myArchive && myArchive.url){
                window.open(myArchive.url, '_blank', 'noopener,noreferrer');
            }
        } catch (error) {
            console.log(error)
            toggleToast("Erro ao abrir o anexo. ", "error")
        } finally {
            setIdArchiveSelected(null)
            setLoadingOpenArchive(false)
        }
    }

    const isClient = currentUser?.role?.slug === "CLIENTE";

  return (
    <div className='card'>
        <div
          className='card-header border-0 cursor-pointer'
          role='button'
          onClick={() => setIsCardOpen((prev) => !prev)}
          aria-expanded={isCardOpen}
          aria-controls='kt_os_details_archives'
        >
          <div className='card-title m-0' style={{justifyContent: 'space-between', width: '100%'}}>
            <h3 className='fw-bolder m-0'>Anexos ({archives.length})</h3>
          </div>
        </div>

        <div id='kt_os_details_archives' className={`collapse ${isCardOpen ? 'show' : ''}`}>
          <div className='card-body border-top p-9'>
            <div className='border border-gray-200 border-dashed rounded p-1'>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(event) => postArchive(event.target.files?.[0])}
                        style={{ display: 'none' }}
                    />
                    <button
                        type='button'
                        disabled={loadingPost}
                        onClick={() => fileInputRef.current?.click()}
                        style={{width: '100%', height: '6rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}
                        className='btn btn-sm btn-light-primary text-hover-white'
                    >
                        {loadingPost ?
                            <Loading myClass='spinner-border' style={{width: '1rem', height: '1rem'}}/> :
                            <KTSVG path='/media/icons/duotune/files/fil009.svg' className='svg-icon-2x' />
                        }
                        <span className='mt-1 fw-semibold fs-7'>Escolha um arquivo.</span>
                    </button>
            </div>

            <div className='border border-gray-300 border-dashed rounded p-4 mt-2' style={{maxHeight: '25rem', overflow: 'auto'}}>
                {loading && (
                    <div className='d-flex justify-content-center p-5'>
                        <Loading myClass='spinner-border' style={{width: '1.5rem', height: '1.5rem'}}/>
                    </div>
                )}

                {!loading && archives.length <= 0 && (
                    <div className='bg-light d-flex flex-column align-items-center justify-content-center p-5' style={{gap: '0.5rem'}}>
                        <KTSVG
                            path="/media/icons/duotune/general/gen004.svg"
                            className='svg-icon-3x me-1'
                        />
                        <span className='text-muted fw-semibold fs-7'>Sem anexos!</span>
                    </div>
                )}

                {!loading && archives.length > 0 && (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.7rem'}}>
                        {archives.map((archive: any) => (
                            <div key={archive.id} className='d-flex' style={{alignItems: 'center', gap: '0.5rem'}}>
                                <div className='symbol symbol-40px symbol-circle'>
                                    <span className='symbol-label bg-light-primary text-primary fs-9 fw-bolder'>
                                        <i className={`bi ${getIconClass(archive.description)} fs-1`}></i>
                                    </span>
                                </div>
                                <div style={{width: '100%'}}>
                                    <div className='d-flex justify-content-start flex-column bg-light p-2'>
                                        <a
                                            href="#"
                                            onClick={(event) => {
                                                event.preventDefault();
                                                openArchive(archive.id);
                                            }}
                                        >
                                            <span className='text-muted fw-semibold text-hover-primary d-block fs-7'>
                                                {archive.description ? archive.description : "Não encontrado"}
                                            </span>
                                        </a>
                                    </div>
                                </div>
                                <div>
                                    {loadingOpenArchive && idArchiveSelected === archive.id ?
                                        <Loading myClass='spinner-border' style={{width: '1rem', height: '1rem'}}/>
                                    :
                                        !isClient &&
                                        <button
                                            type='button'
                                            onClick={() => {
                                                setIdArchiveSelected(archive.id)
                                                setShowModalDelete(true)
                                            }}
                                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                        >
                                            <KTSVG
                                                path='/media/icons/duotune/general/gen027.svg'
                                                className='svg-icon-3'
                                            />
                                        </button>
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
          </div>
        </div>

        <ModalDeleteArquivos
            show={showModalDelete}
            handleClose={() => {
                setShowModalDelete(false)
                setIdArchiveSelected(null)
                refetch()
            }}
            id={idArchiveSelected ? idArchiveSelected : ""}
            data={archives.find((archive: any) => archive.id === idArchiveSelected)}
        />
    </div>
  );
}
